/**
 * General-purpose utility functions for formatting and displaying data
 * Can be used across the application for consistent data presentation
 */

import { CODE_OF_CONDUCT_COURSE_TYPE } from '../constants/customContent';

/**
 * Get the display name for a course type code
 * Converts course type codes to human-readable names
 * 
 * @param courseType - The course type code (e.g., 'cc', 'ec')
 * @returns Human-readable course type name
 * 
 * @example
 * getCourseTypeDisplayName('cc') // returns 'Code of Conduct'
 * getCourseTypeDisplayName('ec') // returns 'Standalone courses'
 * getCourseTypeDisplayName('unknown') // returns 'unknown'
 */
export function getCourseTypeDisplayName(courseType: string): string {
  switch (courseType.toLowerCase()) {
    case 'cc':
      return 'Code of Conduct';
    case 'ec':
      return 'Standalone courses';
    default:
      return courseType;
  }
}

/**
 * Topic name is not applicable for Code of Conduct courses.
 */
export function getDisplayTopicName(
  courseType: string | undefined,
  topicName: string | undefined
): string {
  if (courseType === CODE_OF_CONDUCT_COURSE_TYPE) {
    return '-';
  }
  return topicName || '-';
}

/**
 * Format a date for authoring list tables (e.g. Courses Updated column).
 * @example formatAuthoringListDate('2026-06-18T14:30:00.000Z') // '18 Jun 2026'
 */
export function formatAuthoringListDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Format a date string to a localized format
 * 
 * @param dateString - ISO date string
 * @returns Formatted date string (MM/DD/YYYY HH:mm format)
 * 
 * @example
 * formatDate('2026-01-14T04:54:44.120Z') // returns '01/14/2026 04:54'
 */
export function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).replace(',', '');
  } catch (error) {
    return dateString;
  }
}

/**
 * Capitalize the first letter of a string
 * 
 * @param str - String to capitalize
 * @returns String with first letter capitalized
 * 
 * @example
 * capitalizeFirstLetter('approved') // returns 'Approved'
 */
export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Extract filename from a file path (supports both forward and backslashes)
 * 
 * @param filePath - Full file path or S3 key
 * @returns Extracted filename
 * 
 * @example
 * extractFilename('s3://bucket/folder/file.pdf') // returns 'file.pdf'
 * extractFilename('/path/to/file.pdf') // returns 'file.pdf'
 */
export function extractFilename(filePath: string | null): string {
  if (!filePath) return 'Unknown File';
  // Handle both forward slashes and backslashes
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || 'Unknown File';
}

/**
 * Convert file size in bytes to human-readable format
 * 
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 * 
 * @example
 * formatFileSize(1024) // returns '1 KB'
 * formatFileSize(1048576) // returns '1 MB'
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined || bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Get file size in bytes (for components that expect number)
 * 
 * @param bytes - File size in bytes or null
 * @returns File size in bytes or 0 if null
 */
export function getFileSizeInBytes(bytes: number | null): number {
  if (bytes === null || bytes === undefined) {
    return 0;
  }
  return bytes;
}

/**
 * Get status chip styling based on status value
 * Returns appropriate colors for different status states
 * 
 * @param status - The status string (e.g., 'published', 'approved', 'submitted')
 * @param isArchived - Whether the item is archived
 * @param tokens - Theme tokens object containing design system values
 * @returns Object with backgroundColor and color properties
 * 
 * Uses the `*.background` tokens for the pill fill and `*.content` tokens for the
 * label. The `accent.*.content` tokens are foreground colors, not fills — using
 * them as `backgroundColor` renders dark-on-dark text.
 *
 * @example
 * getStatusChipStyles('published', false, tokens) // returns blue background
 * getStatusChipStyles('approved', false, tokens) // returns green background
 * getStatusChipStyles('submitted', false, tokens) // returns yellow background
 */
export function getStatusChipStyles(status: string, isArchived: boolean, tokens: any) {
  if (isArchived) {
    return {
      backgroundColor: tokens.semantic.color.background.container.value,
      color: tokens.semantic.color.type.default.value,
      border: 'none',
    };
  }

  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    case 'published':
      return {
        backgroundColor: tokens.semantic.color.accent.blue.background.value,
        color: tokens.semantic.color.accent.blue.content.value,
        border: 'none',
      };
    case 'approved':
      return {
        backgroundColor: tokens.semantic.color.status.success.background.value,
        color: tokens.semantic.color.type.default.value,
        border: 'none',
      };
    case 'submitted':
      return {
        backgroundColor: tokens.semantic.color.accent.yellow.background.value,
        color: tokens.semantic.color.accent.yellow.content.value,
        border: 'none',
      };
    case 'rejected':
      return {
        backgroundColor: tokens.semantic.color.status.error.default.value,
        color: tokens.semantic.color.type.inverse.value,
        border: 'none',
      };
    default:
      return {
        backgroundColor: tokens.semantic.color.background.container.value,
        color: tokens.semantic.color.type.default.value,
        border: 'none',
      };
  }
}

/**
 * Extract format type from a file name or path.
 * For zip files, checks for SCORM or HTML indicators in the name; otherwise returns ZIP.
 * For non-zip files, returns the file extension.
 * 
 * @param fileName - The file name, s3_key, or asset_name to check
 * @returns Format string (e.g., 'SCORM', 'HTML', 'ZIP', 'PDF')
 * 
 * @example
 * extractFormat('cbiv-ab_v1_scorm_english.zip') // returns 'SCORM'
 * extractFormat('CBI_GenerativeAI_2ndEd_HTML5_Locked_v1') // returns 'HTML'
 * extractFormat('document.pdf') // returns 'PDF'
 */
export function extractFormat(fileName: string | null | undefined): string {
  if (!fileName) return '-';

  const normalizedFileName = fileName.split('/').pop() ?? fileName;
  const lowerFileName = normalizedFileName.toLowerCase();
  const lastDotIndex = normalizedFileName.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex >= normalizedFileName.length - 1) {
    return '-';
  }

  const extension = normalizedFileName.substring(lastDotIndex + 1).toUpperCase();

  if (extension === 'ZIP') {
    if (lowerFileName.includes('scorm')) {
      return 'SCORM';
    }

    if (lowerFileName.includes('html')) {
      return 'HTML';
    }

    // EthicsLand cartoon bundles (PPT.zip / Cartoons_*.zip) — PPTX in production CE UI
    if (/ppt\.zip$/i.test(normalizedFileName) || /^cartoons_.*\.zip$/i.test(lowerFileName)) {
      return 'PPTX';
    }

    return 'ZIP';
  }

  // If no extension found, return '-'
  return extension;
}

